import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ORANGE = '#f97316';
const ORANGE_DARK = '#ea580c';
const SIDEBAR_BG = '#1c1917';
const SIDEBAR_W = 260;

const NAV_USER = [
  { label: 'Dashboard',          icon: '▦',  to: '/dashboard' },
  { label: 'Press Releases',     icon: '📰', header: true },
  { label: 'Add Press Release',  icon: '✚',  to: '/dashboard/press-release/add' },
  { label: 'View Press Releases',icon: '☰',  to: '/dashboard/press-releases' },
  { label: 'Guest Posts',        icon: '✍',  header: true },
  { label: 'Add Guest Post',     icon: '✚',  to: '/dashboard/guest-post/add' },
  { label: 'View Guest Posts',   icon: '☰',  to: '/dashboard/guest-posts' },
  { label: 'Packages',           icon: '📦', header: true },
  { label: 'PR Packages',        icon: '🗂',  to: '/dashboard/packages/pr' },
  { label: 'Guest Post Packages',icon: '🗂',  to: '/dashboard/packages/guest' },
  { label: 'Account',            icon: '👤', header: true },
  { label: 'My Transactions',    icon: '💳', to: '/dashboard/transactions' },
  { label: 'PR Credits',         icon: '🪙',  to: '/dashboard/pr-credits' },
  { label: 'Guest Credits',      icon: '🪙',  to: '/dashboard/guest-credits' },
  { label: 'Manage Companies',   icon: '🏢', to: '/dashboard/companies' },
  { label: 'My Profile',         icon: '⚙',  to: '/dashboard/profile' },
  { label: 'Support Tickets',    icon: '🎫', to: '/dashboard/support' },
];

const NAV_ADMIN_EXTRA = [
  { label: 'Admin',              icon: '🔐', header: true },
  { label: 'Admin Dashboard',    icon: '▦',  to: '/admin' },
  { label: 'Manage Users',       icon: '👥', to: '/admin/users' },
  { label: 'PR Packages',        icon: '📦', to: '/admin/packages/pr' },
  { label: 'Guest Packages',     icon: '📦', to: '/admin/packages/guest' },
  { label: 'All Press Releases', icon: '📰', to: '/admin/press-releases' },
  { label: 'All Guest Posts',    icon: '✍',  to: '/admin/guest-posts' },
];

export default function DashboardLayout() {
  const { currentUser, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 900);

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth < 900); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => { if (isMobile) setSidebarOpen(false); }, [location.pathname]);

  const isAdmin  = currentUser?.role === 'admin';
  const navItems = isAdmin ? [...NAV_USER, ...NAV_ADMIN_EXTRA] : NAV_USER;

  const sidebarVisible = !isMobile || sidebarOpen;

  async function handleLogout() { logout(); navigate('/login'); }

  return (
    <div style={s.shell}>

      {/* ── Sidebar ── */}
      <aside style={{
        ...s.sidebar,
        transform: sidebarVisible ? 'translateX(0)' : `translateX(-${SIDEBAR_W}px)`,
        transition: 'transform 0.25s ease',
      }}>
        <div style={s.sidebarLogo}>
          <span style={s.logoIcon}>🍇</span>
          <span style={s.logoText}>Coneberry</span>
        </div>

        <nav style={s.nav}>
          {navItems.map((item, i) => {
            if (item.header) return <div key={i} style={s.navSection}>{item.label}</div>;
            const active = location.pathname === item.to;
            return (
              <Link
                key={i}
                to={item.to}
                style={{ ...s.navItem, ...(active ? s.navItemActive : {}) }}
              >
                <span style={s.navIcon}>{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div style={s.overlay} onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Main ── */}
      <div style={{ ...s.main, marginLeft: isMobile ? 0 : SIDEBAR_W }}>

        {/* Header */}
        <header style={s.header}>
          <button style={s.menuBtn} onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen && isMobile ? '✕' : '☰'}
          </button>

          <div style={s.headerTitle}>
            {isAdmin ? 'Admin Panel' : 'User Panel'}
          </div>

          <div style={s.headerRight}>
            <Link to="/" style={s.headerLink} title="Public Newsroom">🌐</Link>
            <div style={s.userChip}>
              <div style={s.avatar}>
                {(currentUser?.displayName || currentUser?.email || 'U')[0].toUpperCase()}
              </div>
              <span style={s.userName}>{currentUser?.displayName || currentUser?.email}</span>
              <button onClick={handleLogout} style={s.logoutBtn}>Logout</button>
            </div>
          </div>
        </header>

        {/* Banner */}
        <div style={s.banner}>
          🍇 Welcome to Coneberry — Submit and manage your press releases and guest posts.
        </div>

        {/* Content */}
        <div style={s.content}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

const s = {
  shell: { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif", background: '#f5f6f8' },

  sidebar: {
    width: SIDEBAR_W, minWidth: SIDEBAR_W,
    background: SIDEBAR_BG,
    color: '#fff',
    position: 'fixed', top: 0, left: 0,
    height: '100vh', overflowY: 'auto',
    zIndex: 200,
    display: 'flex', flexDirection: 'column',
    boxShadow: '2px 0 12px rgba(0,0,0,0.15)',
  },

  sidebarLogo: {
    height: 64, padding: '0 1.25rem',
    display: 'flex', alignItems: 'center', gap: '0.6rem',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.04)',
  },
  logoIcon: { fontSize: '1.4rem' },
  logoText: { fontSize: '1.2rem', fontWeight: 800, color: ORANGE, letterSpacing: '0.01em' },

  nav: { padding: '0.75rem 0', flex: 1 },
  navSection: {
    padding: '1rem 1.25rem 0.3rem',
    fontSize: '0.65rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.1em',
    color: 'rgba(255,255,255,0.3)',
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: '0.65rem',
    padding: '0.65rem 1.25rem',
    color: 'rgba(255,255,255,0.75)',
    textDecoration: 'none', fontSize: '0.875rem', fontWeight: 500,
    borderLeft: '3px solid transparent',
    transition: 'background 0.15s, color 0.15s',
  },
  navItemActive: {
    background: 'rgba(249,115,22,0.15)',
    color: ORANGE,
    borderLeft: `3px solid ${ORANGE}`,
    fontWeight: 600,
  },
  navIcon: { fontSize: '0.95rem', width: '1.1rem', textAlign: 'center', flexShrink: 0 },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 199 },

  main: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' },

  header: {
    height: 64, background: '#fff',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center',
    padding: '0 1.5rem', gap: '1rem',
    position: 'sticky', top: 0, zIndex: 50,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  menuBtn: {
    background: 'none', border: 'none',
    fontSize: '1.4rem', cursor: 'pointer',
    color: '#555', padding: '0.2rem 0.4rem',
    borderRadius: 4, lineHeight: 1,
    flexShrink: 0,
  },
  headerTitle: { fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', flex: 1 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '1rem' },
  headerLink: { color: '#888', textDecoration: 'none', fontSize: '1.1rem' },
  userChip: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  avatar: {
    width: 34, height: 34, borderRadius: '50%',
    background: ORANGE, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: '0.9rem', flexShrink: 0,
  },
  userName: { fontSize: '0.85rem', color: '#444', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  logoutBtn: { background: 'none', border: '1px solid #ddd', borderRadius: 4, padding: '0.25rem 0.65rem', fontSize: '0.8rem', cursor: 'pointer', color: '#666', whiteSpace: 'nowrap' },

  banner: {
    background: ORANGE, color: '#fff',
    fontSize: '0.82rem', fontWeight: 500,
    padding: '0 1.5rem', height: 38,
    display: 'flex', alignItems: 'center',
  },

  content: { padding: '1.75rem', flex: 1 },
};
